// mobile/src/lib/watermark.ts

export interface WatermarkData {
  promoterName: string;
  storeName: string;
  companyLogoUrl?: string;
  latitude: number;
  longitude: number;
  timestamp: Date;
}

/**
 * Render watermark onto a canvas that already has the photo drawn.
 * Call AFTER drawImage() and BEFORE toBlob()/toDataURL().
 */
export function renderWatermark(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  data: WatermarkData
): void {
  const { width, height } = canvas;

  // --- Bottom bar (semi-transparent black) ---
  const barHeight = Math.max(60, height * 0.08);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.fillRect(0, height - barHeight, width, barHeight);

  // Text styling
  const fontSize = Math.max(12, Math.round(height * 0.018));
  ctx.font = `bold ${fontSize}px sans-serif`;
  ctx.fillStyle = '#FFFFFF';
  ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
  ctx.shadowBlur = 2;
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 1;

  const padding = 12;
  const lineHeight = fontSize + 4;

  // Line 1: Promoter | Store
  const line1 = `${data.promoterName} | ${data.storeName}`;
  ctx.fillText(line1, padding, height - barHeight + lineHeight);

  // Line 2: Date/Time | GPS
  const dt = data.timestamp;
  const dateStr = `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}:${String(dt.getSeconds()).padStart(2, '0')}`;
  const gpsStr = `Lat: ${data.latitude.toFixed(4)} Lng: ${data.longitude.toFixed(4)}`;
  const line2 = `${dateStr} | ${gpsStr}`;
  ctx.fillText(line2, padding, height - barHeight + lineHeight * 2);

  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
}

/**
 * Draw company logo on canvas (top-left, 15% opacity).
 */
export function drawLogoOnCanvas(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  logoUrl: string
): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const logoSize = Math.min(canvas.width * 0.15, 120);
      const aspectRatio = img.width / img.height;
      const logoW = logoSize;
      const logoH = logoSize / aspectRatio;

      ctx.globalAlpha = 0.15;
      ctx.drawImage(img, 12, 12, logoW, logoH);
      ctx.globalAlpha = 1.0;
      resolve();
    };
    img.onerror = () => resolve(); // Skip logo on error
    img.src = logoUrl;
  });
}

/**
 * Full pipeline: draw photo frame, add logo, add watermark, return JPEG blob.
 */
export async function createWatermarkedPhoto(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  data: WatermarkData
): Promise<Blob> {
  const ctx = canvas.getContext('2d')!;
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;

  // 1. Draw video frame
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  // 2. Draw logo if available
  if (data.companyLogoUrl) {
    await drawLogoOnCanvas(canvas, ctx, data.companyLogoUrl);
  }

  // 3. Render text watermark
  renderWatermark(canvas, ctx, data);

  // 4. Export as JPEG blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create JPEG blob'));
      },
      'image/jpeg',
      0.9
    );
  });
}

/**
 * Full pipeline for gallery images: draw HTMLImageElement onto canvas,
 * add logo, add watermark, return JPEG blob.
 * Mirrors createWatermarkedPhoto but accepts an already-loaded Image element.
 */
export async function createWatermarkedPhotoFromImage(
  img: HTMLImageElement,
  canvas: HTMLCanvasElement,
  data: WatermarkData
): Promise<Blob> {
  const ctx = canvas.getContext('2d')!;
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;

  // 1. Draw image
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  // 2. Draw logo if available
  if (data.companyLogoUrl) {
    await drawLogoOnCanvas(canvas, ctx, data.companyLogoUrl);
  }

  // 3. Render text watermark
  renderWatermark(canvas, ctx, data);

  // 4. Export as JPEG blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to create JPEG blob'));
      },
      'image/jpeg',
      0.9
    );
  });
}
